/**
 * InstallerExecutionView (P12.2)
 *
 * Single source of truth for the installer/subcontractor execution-first
 * experience. Wraps the canonical `PhaseExecutionPanel`, surfaces a single
 * Next Action card, and keeps secondary context (Details / Materials /
 * History / Spec) collapsed by default for mobile-first focus.
 *
 * Hard rules:
 *   - No new execution engine. Reuses PhaseExecutionPanel + JobSpec.
 *   - No contractValue exposure. Payout via derivePayoutView only.
 *   - No operator/admin controls.
 *   - Observer Mode lockdown handled by the parent surface (fieldset).
 */

import { useEffect, useState } from "react";
import {
  ChevronLeft,
  MapPin,
  Calendar,
  ClipboardCheck,
  DollarSign,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Camera,
  AlertOctagon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReportMaterialProblemDialog } from "@/components/procurement/ReportMaterialProblemDialog";
import { FieldMaterialIssuesChip } from "@/components/procurement/FieldMaterialIssuesChip";
import { RequestSubstitutionDialog } from "@/components/procurement/RequestSubstitutionDialog";
import { SubstitutionStatusChip } from "@/components/procurement/SubstitutionStatusChip";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import { PhaseExecutionPanel } from "@/components/execution/PhaseExecutionPanel";
import { JobBriefBlock } from "@/components/contractor/JobBriefBlock";
import { getJobBriefing } from "@/data/contractor/jobBriefing";
import {
  derivePayoutView,
  getActivePhaseExec,
  PORTAL_PAYOUT_STATUS_LABEL,
} from "@/data/portal/contractorPortalSelectors";
import { getApprovedProjectPlan, logPlanningObservation } from "@/data/planningReview";
import { PendingReviewBlock } from "@/components/execution/PendingReviewBlock";
import { useRole } from "@/data/RoleContext";
import { useObserverMode } from "@/hooks/useObserverMode";
import { formatAddress } from "@/lib/addressUtils";
import type { Job } from "@/data/types";
import type { JobSpec } from "@/data/jobSpec";
import type { PhaseExecution } from "@/data/jobSpecExecution";

/* ── Next Action derivation (no contractValue, no admin data) ───────── */

function deriveNextAction(activePhase?: PhaseExecution): {
  title: string;
  detail: string;
  tone: "primary" | "warning" | "info" | "success";
} {
  if (!activePhase) {
    return {
      title: "Awaiting QA review",
      detail: "All phases submitted. No action needed right now.",
      tone: "success",
    };
  }
  switch (activePhase.status) {
    case "qa_failed":
      return {
        title: `Fix and resubmit ${activePhase.phaseName}`,
        detail:
          activePhase.qaFailReason?.trim() ||
          "QA returned this phase. Review the correction notes and resubmit.",
        tone: "warning",
      };
    case "submitted":
      return {
        title: `Awaiting QA on ${activePhase.phaseName}`,
        detail: "Photos and checklist submitted. Hold tight.",
        tone: "info",
      };
    case "in_progress":
      return {
        title: `Finish ${activePhase.phaseName}`,
        detail: "Complete required photos and checklist items, then submit.",
        tone: "primary",
      };
    case "not_started":
    default:
      return {
        title: `Start ${activePhase.phaseName}`,
        detail: "Take before photos and begin the phase checklist.",
        tone: "primary",
      };
  }
}

const TONE_CLS: Record<
  ReturnType<typeof deriveNextAction>["tone"],
  { card: string; chip: string; icon: typeof Camera }
> = {
  primary: {
    card: "border-primary/30 bg-primary/[0.06]",
    chip: "bg-primary/15 text-primary border-primary/30",
    icon: Camera,
  },
  warning: {
    card: "border-warning/40 bg-warning/[0.06]",
    chip: "bg-warning/15 text-warning border-warning/30",
    icon: ClipboardCheck,
  },
  info: {
    card: "border-info/30 bg-info/[0.05]",
    chip: "bg-info/15 text-info border-info/30",
    icon: ClipboardCheck,
  },
  success: {
    card: "border-success/30 bg-success/[0.05]",
    chip: "bg-success/15 text-success border-success/30",
    icon: CheckCircle2,
  },
};

/* ── Collapsible section ───────────────────────────────────────────── */

function CollapsibleSection({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`installer-exec-section-${id}`}
      data-open={open ? "true" : "false"}
      className="rounded-xl border border-border bg-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3.5 py-2.5 text-left"
        aria-expanded={open}
      >
        <span className="text-xs font-semibold text-foreground/90 uppercase tracking-wider">
          {title}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {open && <div className="px-3.5 pb-3.5 pt-1">{children}</div>}
    </div>
  );
}

/* ── PayoutStatusBadge ─────────────────────────────────────────────── */

function PayoutStatusBadge({
  status,
}: {
  status: keyof typeof PORTAL_PAYOUT_STATUS_LABEL;
}) {
  const cls: Record<typeof status, string> = {
    pending_qa: "bg-muted text-muted-foreground border-border",
    approved: "bg-info/10 text-info border-info/20",
    paid: "bg-success/10 text-success border-success/20",
  };
  return (
    <Badge variant="outline" className={`text-[10px] ${cls[status]}`}>
      {PORTAL_PAYOUT_STATUS_LABEL[status]}
    </Badge>
  );
}

/* ── InstallerExecutionView ────────────────────────────────────────── */

export interface InstallerExecutionViewProps {
  job: Job;
  spec: JobSpec;
  /** Optional back affordance — present when embedded in ContractorPortal. */
  onBack?: () => void;
}

export function InstallerExecutionView({
  job,
  spec,
  onBack,
}: InstallerExecutionViewProps) {
  const { specExecutions, payProfiles } = useAppData();
  const { id: contractorId, matchKey, displayName } = useCurrentContractor();
  const exec = specExecutions.find((e) => e.jobId === job.id);
  const activePhaseExec = getActivePhaseExec(exec);
  const activePhaseName = activePhaseExec?.phaseName;
  const [reportOpen, setReportOpen] = useState(false);
  const [subOpen, setSubOpen] = useState(false);
  const payout = derivePayoutView({
    job,
    contractorId,
    payProfiles,
    execution: exec,
  });

  const canInteract =
    job.assignedSubcontractor === matchKey || job.assignedCrew === matchKey;

  const nextAction = deriveNextAction(activePhaseExec);
  const tone = TONE_CLS[nextAction.tone];
  const NextIcon = tone.icon;

  // Phase progress derived from execution.
  const totalPhases = exec?.phases.length ?? spec.phases?.length ?? 0;
  const passedPhases =
    exec?.phases.filter((p) => p.status === "qa_passed").length ?? 0;
  const jobLabel = typeof job.jobNumber === "string" && job.jobNumber.trim()
    ? job.jobNumber.trim()
    : job.id;
  const brief = getJobBriefing(job, spec);
  const approved = getApprovedProjectPlan(job, spec);
  const isPendingReview = approved.reviewStatus !== "approved";

  // P12.5 — log pending-review block view (fail-silent, deduped).
  const { role } = useRole();
  const { isObserverMode } = useObserverMode("installer");
  useEffect(() => {
    if (!isPendingReview) return;
    logPlanningObservation(
      "pending_block_viewed",
      job.id,
      role || "unknown",
      {},
      { observer: isObserverMode },
    );
  }, [isPendingReview, job.id, role, isObserverMode]);

  if (isPendingReview) {
    return (
      <div
        data-testid="installer-execution-view"
        data-job-id={job.id}
        data-review-pending="true"
        className="space-y-4"
      >
        {onBack && (
          <button
            onClick={onBack}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back to jobs
          </button>
        )}
        <Card>
          <CardContent className="p-4">
            <p className="text-[10px] font-mono text-muted-foreground">{jobLabel}</p>
            <h2 className="text-base font-bold mt-0.5">{job.customerName}</h2>
            <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
              <MapPin className="h-3 w-3" />
              {formatAddress(job.address) || "Address TBD"}
            </p>
          </CardContent>
        </Card>
        <PendingReviewBlock jobId={job.id} />
      </div>
    );
  }

  return (
    <div
      data-testid="installer-execution-view"
      data-job-id={job.id}
      className="space-y-4"
    >
      {onBack && (
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Back to jobs
        </button>
      )}

      {/* A. Context header */}
      <Card data-testid="installer-exec-header">
        <CardContent className="p-4">
          <p className="text-[10px] font-mono text-muted-foreground">{jobLabel}</p>
          <h2 className="text-base font-bold mt-0.5">{job.customerName}</h2>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-1">
            <MapPin className="h-3 w-3" />
            {formatAddress(job.address) || "Address TBD"}
          </p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground flex-wrap">
            {job.scheduledInstallDate && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {job.scheduledInstallDate}
              </span>
            )}
            {activePhaseName && (
              <Badge variant="outline" className="text-[10px]">
                Phase: {activePhaseName}
              </Badge>
            )}
            <span className="font-mono">{spec.label}</span>
          </div>
          {!canInteract && (
            <p className="mt-2 text-[10px] text-warning">
              Read-only view — this job isn't assigned to you.
            </p>
          )}
        </CardContent>
      </Card>

      {/* A.1 Job Brief — arrival, days, crew, risk flags */}
      <JobBriefBlock brief={brief} />

      {/* B. Next Action card */}
      <Card
        data-testid="installer-next-action"
        data-tone={nextAction.tone}
        className={`border ${tone.card}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div
              className={`h-9 w-9 rounded-lg border flex items-center justify-center shrink-0 ${tone.chip}`}
            >
              <NextIcon className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Next action
              </p>
              <p className="text-sm font-bold mt-0.5 leading-snug">
                {nextAction.title}
              </p>
              <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                {nextAction.detail}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* B.1 — Report Material Problem (Phase 4 field feedback entry) */}
      {canInteract && (
        <div className="flex items-center justify-between gap-2 px-0.5 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <FieldMaterialIssuesChip job={job} />
            <SubstitutionStatusChip jobId={job.id} />
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="installer-request-substitution"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-[11px]"
              onClick={() => setSubOpen(true)}
            >
              Request Substitution
            </Button>
            <Button
              data-testid="installer-report-material-problem"
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-[11px]"
              onClick={() => setReportOpen(true)}
            >
              <AlertOctagon className="h-3.5 w-3.5" />
              Report Material Problem
            </Button>
          </div>
        </div>
      )}
      <ReportMaterialProblemDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        jobId={job.id}
        actor={displayName || "Installer"}
        phase={activePhaseName}
      />
      <RequestSubstitutionDialog
        open={subOpen}
        onOpenChange={setSubOpen}
        jobId={job.id}
        actor={displayName || "Installer"}
        defaults={{ reason: "field_issue" }}
      />

      {/* C. Required inputs (canonical execution panel) */}
      <section>
        <div className="flex items-center justify-between mb-2 px-0.5">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <ClipboardCheck className="h-3.5 w-3.5" /> Execution
          </h3>
          {totalPhases > 0 && (
            <span
              data-testid="installer-phase-progress"
              className="text-[10px] font-medium text-muted-foreground"
            >
              {passedPhases}/{totalPhases} phases
            </span>
          )}
        </div>
        <PhaseExecutionPanel
          job={job}
          spec={spec}
          showKitPanel
          activePhaseName={activePhaseName}
        />
      </section>

      {/* D. Payout — contractor-safe only */}
      <section>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5" /> Payout
        </h3>
        <Card>
          <CardContent className="p-4 space-y-2">
            {payout.amount != null ? (
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-muted-foreground">
                  Total payout
                </span>
                <span
                  data-testid="installer-payout-amount"
                  className="text-xl font-bold"
                >
                  ${payout.amount.toLocaleString()}
                </span>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                No active Pay Profile on file.
              </p>
            )}
            <div className="flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground">Structure</span>
              <span className="font-medium">{payout.structureLabel}</span>
            </div>
            <div className="flex items-center justify-between text-[11px] pt-1.5 border-t border-border/60">
              <span className="text-muted-foreground">Status</span>
              <PayoutStatusBadge status={payout.status} />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* E. Secondary sections — collapsed by default */}
      <div className="space-y-2">
        <CollapsibleSection id="details" title="View Details">
          <dl className="text-[11px] space-y-1.5">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Project type</dt>
              <dd className="font-medium text-right">{job.projectType}</dd>
            </div>
            {job.scopeSummary && (
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">Scope</dt>
                <dd className="font-medium text-right max-w-[60%]">
                  {job.scopeSummary}
                </dd>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">Stage</dt>
              <dd className="font-medium text-right">{job.currentStage}</dd>
            </div>
          </dl>
        </CollapsibleSection>

        <CollapsibleSection id="materials" title="View Materials">
          <p className="text-[11px] text-muted-foreground">
            Material kit and BOM are surfaced inside the Execution panel
            above. Open a phase to see required parts.
          </p>
        </CollapsibleSection>

        <CollapsibleSection id="history" title="View History">
          <ul className="text-[11px] space-y-1">
            {(exec?.phases ?? []).map((p) => (
              <li
                key={p.phaseName}
                className="flex justify-between gap-3 border-b border-border/40 pb-1 last:border-0"
              >
                <span className="text-foreground/80">{p.phaseName}</span>
                <span className="text-muted-foreground capitalize">
                  {p.status.replace(/_/g, " ")}
                </span>
              </li>
            ))}
            {(!exec || exec.phases.length === 0) && (
              <li className="text-muted-foreground">No execution history yet.</li>
            )}
          </ul>
        </CollapsibleSection>

        <CollapsibleSection id="spec" title="View Spec">
          <p className="text-[11px] text-muted-foreground">
            Spec <span className="font-mono">{spec.label}</span> with{" "}
            {spec.phases?.length ?? 0} phases.
          </p>
        </CollapsibleSection>
      </div>
    </div>
  );
}

export default InstallerExecutionView;
